import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Modal, Button, message } from 'antd';
import { GiftOutlined } from '@ant-design/icons';
import ConfettiExplosion from 'react-confetti-explosion';
import GiftImage from 'src/_shared/assets/snappy_present.png';
import './styles.scss';

const confettiConfig = {
  force: 0.9,
  duration: 3000,
  particleCount: 300,
  width: '2000',
  height: 1600,
  colors: ['#f650a9', '#00c3ff', '#fffc00', '#00ff87', '#ff0055', '#7700ff']
};

const RecognitionReward = ({ recognition, currentUser, onStateChange, onClaimGift, disableSpotlight = false }) => {
  const [isFirstView, setIsFirstView] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [isExploding, setIsExploding] = useState(false);
  const [rewardState, setRewardState] = useState('unviewed');
  const [spotlightPosition, setSpotlightPosition] = useState({ x: 0, y: 0 });
  const intervalRef = useRef(null);
  const recognitionRef = useRef(null);
  const rewardIconRef = useRef(null);

  const updateSpotlightPosition = () => {
    if (rewardIconRef.current) {
      const rect = rewardIconRef.current.getBoundingClientRect();
      setSpotlightPosition({
        x: rect.left + rect.width / 2,
        y: rect.top + rect.height / 2
      });
    }
  };

  useEffect(() => {
    if (recognition && recognition.status === 'unviewed' && !disableSpotlight) {
      setIsFirstView(true);
      setRewardState('unviewed');
      
      setTimeout(updateSpotlightPosition, 100);
      
      const panInterval = setInterval(() => {
        if (rewardIconRef.current) {
          const rect = rewardIconRef.current.getBoundingClientRect();
          const centerX = rect.left + rect.width / 2;
          const centerY = rect.top + rect.height / 2;
          
          setSpotlightPosition({
            x: centerX + (Math.random() * 6 - 3),
            y: centerY + (Math.random() * 6 - 3)
          });
        }
      }, 2000);

      window.addEventListener('resize', updateSpotlightPosition);
      window.addEventListener('scroll', updateSpotlightPosition);
      
      intervalRef.current = panInterval;
    } else if (recognition) {
      setRewardState(recognition.status);
      setIsFirstView(false);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      // Clean up event listeners
      window.removeEventListener('resize', updateSpotlightPosition);
      window.removeEventListener('scroll', updateSpotlightPosition);
    };
  }, [recognition, disableSpotlight]);

  const handleRewardClick = () => {
    if (rewardState === 'expired') {
      message.info('This reward has expired');
      return;
    }

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (onClaimGift) {
      handleRedeem();
      setIsFirstView(false);
      
      if (rewardState === 'unviewed') {
        setRewardState('viewed');
        if (onStateChange) {
          onStateChange('viewed', recognition.id);
        }
      }
      return;
    }

    setShowModal(true);
    setIsExploding(true);
    setIsFirstView(false);
    
    if (rewardState === 'unviewed') {
      setRewardState('viewed');
      if (onStateChange) {
        onStateChange('viewed', recognition.id);
      }
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setIsExploding(false);
  };

  const handleRedeem = () => {
    if (onClaimGift) {
      const mockActivity = {
        id: recognition.id,
        type: 'gift_received',
        snappyGrantId: 'test-snappy-grant-123',
        rawData: {
          campaignTierId: 'test-campaign-tier-456'
        }
      };
      onClaimGift(mockActivity);
      setShowModal(false);
      return;
    }

    if (recognition && recognition.giftDetails && recognition.giftDetails.link) {
      window.open(recognition.giftDetails.link, '_blank');
      setRewardState('unwrapped');
      if (onStateChange) {
        onStateChange('unwrapped', recognition.id);
      }
    } else {
      message.error('Unable to open reward link');
    }
    setShowModal(false);
  };

  const renderRewardIcon = () => {
    if (rewardState === 'expired') {
      return (
        <div className="recognition-reward-icon expired" ref={rewardIconRef}>
          <GiftOutlined />
          <div className="expired-overlay">
            <span>😞</span>
          </div>
        </div>
      );
    }

    return (
      <motion.div
        ref={rewardIconRef}
        className={`recognition-reward-icon ${rewardState}`}
        animate={rewardState === 'unviewed' ? {
          rotate: [0, -5, 5, -5, 0],
          scale: [1, 1.05, 1, 1.05, 1]
        } : {}}
        transition={{
          repeat: Infinity,
          repeatDelay: 3.0,
          duration: 0.8
        }}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <GiftOutlined />
      </motion.div>
    );
  };

  return (
    <>
      <div className="recognition-reward-container" ref={recognitionRef}>
        <AnimatePresence>
          {isFirstView && !disableSpotlight && (
            <motion.div
              className="spotlight-overlay"
              initial={{ opacity: 0 }}
              animate={{
                opacity: 1,
                WebkitMaskImage: `radial-gradient(circle at ${spotlightPosition.x}px ${spotlightPosition.y}px, transparent 0px, transparent 140px, black 160px)`,
                maskImage: `radial-gradient(circle at ${spotlightPosition.x}px ${spotlightPosition.y}px, transparent 0px, transparent 140px, black 160px)`
              }}
              exit={{ opacity: 0 }}
              transition={{
                duration: 0.8,
                WebkitMaskImage: { type: "spring", stiffness: 100, damping: 15 },
                maskImage: { type: "spring", stiffness: 100, damping: 15 }
              }}
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                pointerEvents: 'none',
                zIndex: 1000,
                background: 'rgba(0, 0, 0, 0.5)',
              }}
            >
            </motion.div>
          )}
        </AnimatePresence>
        
        <motion.div
          className="recognition-reward"
          onClick={handleRewardClick}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          initial={isFirstView ? { scale: 0.9, opacity: 0 } : { scale: 1, opacity: 1 }}
          animate={isFirstView ?
            {
              scale: 1.05,
              opacity: 1,
              boxShadow: "0 0 25px rgba(246, 80, 169, 0.6)"
            } :
            {
              scale: 1,
              opacity: 1
            }
          }
          transition={{
            duration: 0.8,
            type: "spring"
          }}
        >
          {renderRewardIcon()}
          <div className="recognition-reward-info">
            <h3>{recognition?.title || 'New Recognition'}</h3>
            <p>{recognition?.description || 'You received a new recognition!'}</p>
            <p className="recognition-reward-status">
              {rewardState === 'unviewed' && 'New! Click to open'}
              {rewardState === 'viewed' && 'Click to unwrap your gift'}
              {rewardState === 'unwrapped' && 'Gift unwrapped'}
              {rewardState === 'redeemed' && 'Gift redeemed'}
              {rewardState === 'expired' && 'Gift expired'}
            </p>
          </div>
        </motion.div>
      </div>
      

      <Modal
        title="Congratulations!"
        open={showModal}
        onCancel={handleModalClose}
        footer={[
          <Button key="close" onClick={handleModalClose}>
            Close
          </Button>,
          <Button key="redeem" type="primary" onClick={handleRedeem} size="large">
            Go to Snappy and unwrap it!
          </Button>,
        ]}
        className="recognition-reward-modal"
        width={800}
        centered
      >
        {isExploding && (
          <div style={{position: 'absolute', overflow: 'visible',top: '25%', left:'50%'}}>
            <ConfettiExplosion {...confettiConfig} />
         </div>
        )}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="recognition-modal-content"
        >
          <motion.h2
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{
              duration: 0.8,
              delay: 0.4,
              type: "spring",
              stiffness: 100
            }}
            style={{marginTop: 12}}
          >
            Your Recognition has earned you a prize!
          </motion.h2>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <img src={GiftImage} alt="Art of wrapped gift" />
          </motion.div>
        </motion.div>
      </Modal>
    </>
  );
};

export default RecognitionReward;